import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  SetStateAction,
  Dispatch,
  ReactNode,
} from 'react';
import { UserPointsType } from '../types/userPoints.type';
import { axiosInstance } from '../Helper/Api/axios.instance';
import { AppConfig } from '../Config/app-config';
import { UserProfileType } from '../types/userProfile.type';
import { MissionCategoryEnum } from '../types/enums/missionCategory.enum';
import { TokenEnum } from '../types/enums/token.enum';
import { UserAirdropDataType } from '../types/userAirdropData';

interface UserContextType {
  pointsData: UserPointsType;
  profileData: UserProfileType;
  isLoggedIn: boolean;
  airdropData: UserAirdropDataType;
  setIsLoggedIn: Dispatch<SetStateAction<boolean>>;
  handleLogout: () => void;
}

const defaultValue: UserContextType = {
  pointsData: {
    pointsAccumulated: 0,
    currentMultiplier: {
      [MissionCategoryEnum.GENERAL]: 0,
      [MissionCategoryEnum.ETH]: 0,
      [MissionCategoryEnum.STABLECOIN]: 0,
    },
    dailyPoints: 0,
    amountBridged: {
      [TokenEnum.ETH]: 0,
      [TokenEnum.DAI]: 0,
      [TokenEnum.OZUSD]: 0,
      [TokenEnum.USDT]: 0,
    },
    airdropPaused: false,
  },
  profileData: {
    publicId: null,
    referralCode: null,
    twitterId: null,
    telegramId: null,
    invitedByUserId: null,
  },
  airdropData: {
    eligible: false,
    tokenAmount: '0',
    pointsAccumulated: '0',
    proofs: [],
  },
  isLoggedIn: false,
  setIsLoggedIn: () => {},
  handleLogout: () => {},
};

export const lsKey = 'ozAccessToken';
export const signInLockKey = 'wallet_signing';

const UserContext = createContext<UserContextType>(defaultValue);

function UserContextProvider({ children }: { children: ReactNode }) {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [pointsData, setPointsData] = useState(defaultValue.pointsData);
  const [profileData, setProfileData] = useState(defaultValue.profileData);
  const [airdropData, setAirdropData] = useState<UserAirdropDataType>();

  useEffect(() => {
    const lsToken = localStorage.getItem(lsKey);
    if (lsToken) {
      setIsLoggedIn(true);
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      void fetchUserPoints();
      void fetchUserProfile();
      void fetchUserAirdropData();
    } else {
      setPointsData(defaultValue.pointsData);
      setProfileData(defaultValue.profileData);
      setAirdropData(undefined);
    }
  }, [isLoggedIn]);

  const fetchUserPoints = useCallback(async () => {
    try {
      const { data: pointsData } = await axiosInstance.get(
        AppConfig.apiRoutes.getPointsData(),
      );

      setPointsData(pointsData);
    } catch (error) {
      console.log(error);
    }
  }, []);

  const fetchUserProfile = useCallback(async () => {
    try {
      const { data: profileData } = await axiosInstance.get(
        AppConfig.apiRoutes.getUserProfile(),
      );

      console.log(profileData);

      setProfileData(profileData);
    } catch (error) {
      console.log(error);
    }
  }, []);

  const fetchUserAirdropData = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get(
        AppConfig.apiRoutes.getAirdropData(),
      );

      setAirdropData(data);
    } catch (error) {
      console.log(error);
    }
  }, []);

  const handleLogout = useCallback(() => {
    setIsLoggedIn(false);
    localStorage.removeItem(lsKey);
  }, []);

  const contextValue = useMemo(
    () => ({
      profileData,
      pointsData,
      isLoggedIn,
      setIsLoggedIn,
      handleLogout,
      airdropData,
    }),
    [profileData, pointsData, isLoggedIn, setIsLoggedIn, handleLogout],
  );

  return (
    <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>
  );
}

export default UserContextProvider;

export const useUserContext = () => useContext(UserContext);
